import { RootState } from '../../store'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  computeAvailableForBetCombinedExposer,
  computeRemainingBalance,
  computeWithdrawableBalance,
  computeWithdrawableMainCombinedExposer,
} from '../../../utils/availableBalance'

export type HideBalExp = { balance: boolean; exposer: boolean }

export type BalanceRow = {
  balance: number
  exposer: number
  /** Legacy separate bonus wallet — always 0 now (bonus is credited to `balance`). */
  bonusBalance?: number
  /** Lifetime bonus credited to the main balance (display only). */
  bonusReceived?: number
  /** Bonus still to be wagered before it can be withdrawn. */
  bonusLocked?: number
  pendingBonusUsed?: number
  availableBalance?: number
  /** balance − exposure − bonusLocked (server-computed when present). */
  withdrawableBalance?: number
  /** Unsettled PnL since last transfer (wallet Winnings). */
  profitLoss?: number
  /** Live credit limit from User.creditRefrences (admin CRD). */
  creditRefrences?: number
}

export type BalanceState = {
  balance: BalanceRow
  hide: HideBalExp
}

const initialState: BalanceState = {
  balance: {
    balance: 0,
    exposer: 0,
    bonusBalance: 0,
    bonusReceived: 0,
    bonusLocked: 0,
    pendingBonusUsed: 0,
    availableBalance: 0,
    withdrawableBalance: 0,
    profitLoss: 0,
    creditRefrences: undefined,
  },
  hide: { balance: false, exposer: false },
}

const balanceSlice = createSlice({
  name: 'balance',
  initialState,
  reducers: {
    setBalance: (
      state,
      action: PayloadAction<{
        balance: number
        exposer: number
        bonusBalance?: number
        bonusReceived?: number
        bonusLocked?: number
        pendingBonusUsed?: number
        availableBalance?: number
        withdrawableBalance?: number
        profitLoss?: number
        creditRefrences?: number
      }>,
    ) => {
      const balance = action.payload.balance ?? state.balance.balance
      const exposer = action.payload.exposer ?? state.balance.exposer
      const bonusLocked = action.payload.bonusLocked ?? state.balance.bonusLocked ?? 0
      const pendingBonusUsed = action.payload.pendingBonusUsed ?? state.balance.pendingBonusUsed ?? 0
      const next: BalanceRow = {
        balance,
        exposer,
        bonusBalance: action.payload.bonusBalance ?? state.balance.bonusBalance ?? 0,
        bonusReceived: action.payload.bonusReceived ?? state.balance.bonusReceived ?? 0,
        bonusLocked,
        pendingBonusUsed,
        profitLoss:
          action.payload.profitLoss !== undefined
            ? action.payload.profitLoss
            : state.balance.profitLoss ?? 0,
        creditRefrences:
          action.payload.creditRefrences !== undefined
            ? action.payload.creditRefrences
            : state.balance.creditRefrences,
        availableBalance:
          action.payload.availableBalance ??
          computeRemainingBalance({ balance, exposer, pendingBonusUsed }),
        withdrawableBalance:
          action.payload.withdrawableBalance ??
          computeWithdrawableBalance({ balance, exposer, bonusLocked }),
      }
      state.balance = next
    },
    setExposer: (state, action: PayloadAction<number>) => {
      state.balance = { ...state.balance, exposer: action.payload }
    },
    setSingleBal: (state, action: PayloadAction<number>) => {
      state.balance = { ...state.balance, balance: action.payload }
    },
    hideBalExp: (state, action: PayloadAction<HideBalExp>) => {
      state.hide = action.payload
    },
  },
})

export const { setBalance, setExposer, hideBalExp, setSingleBal } = balanceSlice.actions
export const selectBalance = (state: RootState) => state.balanceReducer.balance
export const selectHideBalExp = (state: RootState) => state.balanceReducer.hide

/** Main available after exposure (API merges sports+casino into `exposer`). */
export function computeAvailableForBet(b: {
  balance: number
  exposer: number
  bonusBalance?: number
}): number {
  return computeAvailableForBetCombinedExposer(b)
}

/** Main available after exposure (same as header remaining balance). */
export function computeAvailableMainAfterExposure(b: BalanceRow): number {
  return computeRemainingBalance(b)
}

export { computeRemainingBalance, computeWithdrawableBalance }

export function mapBalanceApiToStore(raw: {
  balance?: number
  exposer?: number
  bonusBalance?: number
  bonusReceived?: number
  bonusLocked?: number
  pendingBonusUsed?: number
  availableBalance?: number
  withdrawableBalance?: number
  profitLoss?: number
  creditRefrences?: number | string
} | null | undefined): BalanceRow | null {
  if (!raw || typeof raw !== 'object' || typeof raw.balance !== 'number') return null
  const profitLoss =
    typeof raw.profitLoss === 'number' && Number.isFinite(raw.profitLoss) ? raw.profitLoss : 0
  const crRaw = raw.creditRefrences
  const creditRefrences =
    crRaw !== undefined && crRaw !== null && crRaw !== '' && Number.isFinite(Number(crRaw))
      ? Number(crRaw)
      : undefined
  const exposer = typeof raw.exposer === 'number' ? raw.exposer : 0
  const bonusLocked = typeof raw.bonusLocked === 'number' ? Math.max(0, raw.bonusLocked) : 0
  const pendingBonusUsed = typeof raw.pendingBonusUsed === 'number' ? raw.pendingBonusUsed : 0
  const row: BalanceRow = {
    balance: raw.balance,
    exposer,
    bonusBalance: typeof raw.bonusBalance === 'number' ? raw.bonusBalance : 0,
    bonusReceived: typeof raw.bonusReceived === 'number' ? raw.bonusReceived : 0,
    bonusLocked,
    pendingBonusUsed,
    profitLoss,
    creditRefrences,
    availableBalance:
      typeof raw.availableBalance === 'number'
        ? raw.availableBalance
        : computeRemainingBalance({ balance: raw.balance, exposer, pendingBonusUsed }),
    withdrawableBalance:
      typeof raw.withdrawableBalance === 'number'
        ? raw.withdrawableBalance
        : computeWithdrawableBalance({ balance: raw.balance, exposer, bonusLocked }),
  }
  return row
}

export default balanceSlice
